import { ApiClient } from '@api/api-configuration';
import type { ExternalProviderServicePopulated } from '@api/READ_ONLY/caregiver_api/Api';
import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import { SelectProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** The fields this picker reads; a visit's `externalProvider` (id + name) is enough to label a value. */
export interface ExternalProviderChoice {
  externalProviderId: number;
  name: string;
  services?: ExternalProviderServicePopulated[];
}

// The endpoint rejects a query shorter than this with a 422.
const MIN_QUERY_LENGTH = 2;

type ExternalProviderSelectProps = {
  onProviderChange?: (provider: ExternalProviderChoice | undefined) => void;
  /** Providers offering any of these services are listed first on each page. */
  preferServiceIds?: number[];
  /** The provider the record already holds, so its name shows before any page is fetched. */
  selectedProvider?: ExternalProviderChoice | null;
  pageSize?: number;
  placeholder?: string;
  /** Also list deactivated providers, e.g. to pay one the agency still owes. */
  includeInactive?: boolean;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch' | 'mode' | 'placeholder'>;

const offersAny = (provider: ExternalProviderChoice, serviceIds: Set<number>) =>
  (provider.services ?? []).some((service) => serviceIds.has(service.serviceId));

/** One page of active providers (all with `includeInactive`); those offering any of `preferServiceIds` come first. */
export const fetchExternalProviders = async (
  params: ApiQueryParams,
  preferServiceIds: number[],
  includeInactive = false
) => {
  const query = params.query?.trim();
  const res = await ApiClient.caregiverApi.externalProviders.listExternalProviders({
    ...(includeInactive ? {} : { active: true }),
    limit: params.limit,
    offset: params.offset,
    ...(query && query.length >= MIN_QUERY_LENGTH ? { query } : {}),
  });
  const preferred = new Set(preferServiceIds);
  const data = preferred.size
    ? [...res.data.data].sort((a, b) => Number(offersAny(b, preferred)) - Number(offersAny(a, preferred)))
    : res.data.data;
  return { data: { data: data as ExternalProviderChoice[], total: res.data.total, offset: res.data.offset } };
};

export const ExternalProviderSelect = ({
  onProviderChange,
  preferServiceIds,
  selectedProvider,
  pageSize = 20,
  placeholder,
  includeInactive = false,
  ...selectProps
}: ExternalProviderSelectProps) => {
  const { t } = useTranslation();
  const serviceKey = preferServiceIds?.join(',') ?? '';

  const fetchProviders = useMemo<ApiFetchFunction<ExternalProviderChoice>>(
    () => (params: ApiQueryParams) =>
      fetchExternalProviders(params, serviceKey ? serviceKey.split(',').map(Number) : [], includeInactive),
    [serviceKey, includeInactive]
  );

  return (
    <GenericSearchSelect<ExternalProviderChoice>
      {...selectProps}
      apiFetch={fetchProviders}
      getItemId={(provider) => provider.externalProviderId}
      getItemLabel={(provider) => provider.name}
      onSelectionChange={(selection) =>
        onProviderChange?.(Array.isArray(selection) ? selection[0] : (selection as ExternalProviderChoice | undefined))
      }
      placeholder={placeholder ?? t('external-provider-select-placeholder')}
      pageSize={pageSize}
      minSearchLength={MIN_QUERY_LENGTH}
      selectedItemsProvider={() => selectedProvider ?? undefined}
      lazyLoadOnOpen
    />
  );
};

export default ExternalProviderSelect;
